import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ObservabilityPage(): Node {
  return html`
    <div>
      <h1>Observability</h1>
      <p><code>createObserver</code> provides structured logging, metrics collection, and performance tracking for production applications.</p>

      <h2>Setup</h2>
      ${CodeBlock(`import { createObserver } from 'onefold/observe';

const observer = createObserver({
  onLog: (level, message, data) => {
    // Send to your logging service
    console.log(\`[\${level}] \${message}\`, data);
  },
  onMetric: (name, value, tags) => {
    // Send to metrics backend (Datadog, Prometheus, etc.)
    analytics.track(name, { value, ...tags });
  },
  onError: (error, context) => {
    // Send to error tracking (Sentry, Bugsnag, etc.)
    errorTracker.captureException(error, { extra: context });
  },
});`)}

      <h2>Logging</h2>
      ${CodeBlock(`observer.log('info', 'User logged in', { userId: '123' });
observer.log('warn', 'API response slow', { duration: 2500 });
observer.log('error', 'Payment failed', { code: 'CARD_DECLINED' });`)}

      <h2>Metrics</h2>
      ${CodeBlock(`// Track custom metrics
observer.metric('page-load', 1.2, { route: '/dashboard' });
observer.metric('api-latency', 340, { endpoint: '/users' });
observer.metric('bundle-size', 48000, { chunk: 'main' });`)}

      <h2>Performance Tracking</h2>
      ${CodeBlock(`// Time an operation
const end = observer.startTimer('fetch-users');
const users = await http.get('/users');
end(); // automatically records duration as a metric`)}

      ${Callout('createObserver is a thin abstraction. It does not bundle any specific logging/metrics library — you wire it to your own backend.')}

      <h2>Integration Example</h2>
      ${CodeBlock(`// Sentry + Datadog integration
const observer = createObserver({
  onLog: (level, msg, data) => {
    if (level === 'error') Sentry.captureMessage(msg, { extra: data });
  },
  onMetric: (name, value, tags) => {
    datadogRum.addTiming(name, value);
  },
  onError: (error, ctx) => {
    Sentry.captureException(error, { contexts: { app: ctx } });
  },
});`)}

      <h2>API</h2>
      <table>
        <tr><th>Method</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>.log</code></td><td>(level, message, data?)</td><td>Structured log entry.</td></tr>
        <tr><td><code>.metric</code></td><td>(name, value, tags?)</td><td>Record a metric.</td></tr>
        <tr><td><code>.startTimer</code></td><td>(name)</td><td>Start timing, returns end() function.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Emit events and see them collected by the observer in real time:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { createObserver } from 'onefold/observe';

function App() {
  const logs = createSignal([]);
  const log = (msg) => logs.set(prev => [...prev.slice(-10), msg]);

  const observer = createObserver();

  // Subscribe to all event types
  observer.on('navigate', (e) => log('[nav] ' + e.from + ' → ' + e.to));
  observer.on('error', (e) => log('[error] ' + e.context + ': ' + e.error));
  observer.on('metric', (e) => log('[metric] ' + e.name + ' = ' + e.value.toFixed(2)));
  observer.on('render', (e) => log('[render] ' + e.component + ' in ' + e.duration.toFixed(1) + 'ms'));
  observer.on('log', (e) => log('[' + e.level + '] ' + e.message));

  return html\`
    <div>
      <h3>Observability — createObserver</h3>
      <p style="font-size:13px;color:#666;margin-bottom:12px">Click buttons to emit structured events:</p>

      <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
        <button onclick=\${() => observer.emit('navigate', { from: '/', to: '/about' })}>Navigate</button>
        <button onclick=\${() => observer.emit('error', { error: 'Connection timeout', context: 'api.fetch' })}>Error</button>
        <button onclick=\${() => observer.metric('page.load', Math.random() * 500)}>Metric</button>
        <button onclick=\${() => observer.log('info', 'User clicked checkout')}>Log</button>
        <button onclick=\${() => {
          const result = observer.trackRender('UserList', () => {
            let sum = 0; for (let i = 0; i < 100000; i++) sum += i;
            return sum;
          });
        }}>Track Render</button>
        <button onclick=\${() => {
          observer.trackError(() => { throw new Error('Oops!'); }, 'risky-op');
        }}>Track Error</button>
      </div>

      <div style="background:#0f172a;color:#a5f3fc;padding:12px;border-radius:8px;font-family:monospace;font-size:12px;max-height:200px;overflow-y:auto">
        \${() => logs().length === 0
          ? html\`<div style="color:#64748b">Events will appear here...</div>\`
          : logs().map(l => html\`<div style="padding:2px 0">> \${l}</div>\`)
        }
      </div>
      <p style="font-size:11px;color:#94a3b8;margin-top:8px">In production, connect these events to Datadog, Sentry, New Relic, etc.</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createObserver — Structured Event Bus')}
    </div>
  `;
}
